import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Target, Heart, Shield, ArrowRight, ArrowUpRight, Sparkles, Building2, Users } from "lucide-react";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.5 }
};

const values = [
  {
    icon: Target,
    title: "Impact concret",
    desc: "Chaque action doit apporter un résultat mesurable. Pas de théorie, que des solutions qui fonctionnent."
  },
  {
    icon: Heart,
    title: "Accessibilité",
    desc: "Des technologies puissantes rendues simples et utilisables par tous, sans jargon technique."
  },
  {
    icon: Shield,
    title: "Confiance",
    desc: "Un accompagnement transparent, honnête et aligné avec les intérêts de nos clients."
  },
  {
    icon: Sparkles,
    title: "Innovation utile",
    desc: "Nous intégrons l'IA et l'automatisation uniquement quand elles apportent une vraie valeur ajoutée."
  }
];

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      {/* Header */}
      <section className="pt-32 pb-16 px-6 md:px-12 lg:px-16 bg-[#0B1F3B] relative overflow-hidden">
        <div className="absolute top-10 left-0 w-80 h-80 bg-[#59C3A6]/5 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div {...fadeUp}>
            <span className="text-[#59C3A6] text-sm font-semibold uppercase tracking-wider">A propos</span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mt-3 mb-6 text-white">
              Notre mission :{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#59C3A6] to-[#4AB095]">
                votre avantage compétitif
              </span>
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#0B1F3B]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <motion.div {...fadeUp}>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-6">
                Donner aux petites entreprises les outils des grandes
              </h2>
              <div className="space-y-5 text-slate-300 text-base leading-relaxed">
                <p>
                  Les grandes entreprises investissent massivement dans la digitalisation, l'automatisation et l'intelligence artificielle. Elles ont des équipes dédiées, des budgets conséquents et un accès aux meilleures technologies.
                </p>
                <p>
                  Les artisans et les petites entreprises, eux, n'ont ni le temps ni les ressources pour mettre en place ces outils. Pourtant, ce sont souvent ceux qui en auraient le plus besoin.
                </p>
                <p>
                  <strong className="text-white">Finovéo Conseil</strong> existe pour combler cet écart. Nous rendons les technologies avancées accessibles, utiles et adaptées aux réalités des entrepreneurs et des artisans.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="space-y-6"
            >
              {/* Stats-like blocks */}
              <div className="p-6 rounded-2xl bg-[#112A4F]/30 border border-white/5">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#59C3A6]/10 flex items-center justify-center shrink-0">
                    <Building2 size={22} className="text-[#59C3A6]" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-white mb-2">Architecte digital</h3>
                    <p className="text-slate-400 text-sm leading-relaxed">
                      Nous ne sommes pas un simple prestataire technique. Nous concevons l'architecture digitale de votre entreprise avec une vision stratégique globale.
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-6 rounded-2xl bg-[#112A4F]/30 border border-white/5">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#59C3A6]/10 flex items-center justify-center shrink-0">
                    <Users size={22} className="text-[#59C3A6]" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-white mb-2">Partenaire de confiance</h3>
                    <p className="text-slate-400 text-sm leading-relaxed">
                      Un interlocuteur unique qui comprend votre métier, parle votre langage et vous accompagne dans la durée.
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-6 rounded-2xl bg-[#112A4F]/30 border border-[#59C3A6]/15">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#59C3A6]/10 flex items-center justify-center shrink-0">
                    <Sparkles size={22} className="text-[#59C3A6]" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-white mb-2">Innovation pragmatique</h3>
                    <p className="text-slate-400 text-sm leading-relaxed">
                      L'IA et l'automatisation au service de résultats concrets : plus de temps, plus de clients, plus de rentabilité.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#071428]">
        <div className="max-w-7xl mx-auto">
          <motion.div {...fadeUp} className="mb-12">
            <span className="text-[#59C3A6] text-sm font-semibold uppercase tracking-wider">Nos valeurs</span>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mt-3">
              Ce qui guide nos décisions
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {values.map((value, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                data-testid={`value-card-${i}`}
                className="p-8 rounded-2xl bg-[#0B1F3B]/60 border border-white/5 hover:border-[#59C3A6]/15 transition-all duration-300"
              >
                <div className="w-11 h-11 rounded-xl bg-[#59C3A6]/10 flex items-center justify-center mb-4">
                  <value.icon size={20} className="text-[#59C3A6]" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-white mb-3">{value.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{value.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#0B1F3B] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-[#59C3A6]/5 rounded-full blur-3xl" />
        </div>
        <motion.div {...fadeUp} className="max-w-3xl mx-auto text-center relative z-10">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-5">
            Échangeons sur votre projet
          </h2>
          <p className="text-slate-300 text-base md:text-lg mb-8 leading-relaxed">
            Chaque entreprise est unique. Discutons de vos enjeux pour définir ensemble les meilleures solutions.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/contact">
              <Button
                data-testid="about-cta"
                className="bg-[#59C3A6] text-[#0B1F3B] hover:bg-[#4AB095] font-semibold px-8 py-3 h-auto rounded-full transition-all duration-300 hover:scale-105 shadow-[0_0_30px_rgba(89,195,166,0.25)] text-base"
              >
                Demander un diagnostic <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
            <a href="https://calendly.com/finoveo-conseil/30min" target="_blank" rel="noopener noreferrer">
              <Button
                variant="outline"
                className="border-[#59C3A6]/30 text-[#59C3A6] hover:bg-[#59C3A6]/10 hover:text-[#59C3A6] px-8 py-3 h-auto rounded-full transition-all duration-300 text-base bg-transparent"
              >
                Réserver un appel <ArrowUpRight className="ml-1" size={16} />
              </Button>
            </a>
          </div>
        </motion.div>
      </section>
    </div>
  );
}
